/**
 * 004 — Mapa de estilos → capa de tokens del DS.
 * Cada estilo aplica la clase `theme-<id>` en el contenedor raíz del render;
 * la cascada base→semántico→estilo (tokens CSS del DS) toma paleta/tipografía/ornamentos.
 * El estilo vive EN LA INVITACIÓN (`template_id`), no en el evento (clarify 2026-07-27).
 * Motor extensible (FR-015): añadir un estilo = una entrada aquí + su `.theme-*` en el DS.
 */
import { EstiloId, ESTILOS } from './types';

export interface EstiloMeta {
  id: EstiloId;
  /** Nombre legible para el selector del editor. */
  nombre: string;
  /** Clase de tema del DS que fija la capa de tokens. */
  themeClass: string;
}

export const ESTILO_META: Record<EstiloId, EstiloMeta> = {
  elegante: { id: 'elegante', nombre: 'Elegante', themeClass: 'theme-elegante' },
  rustico: { id: 'rustico', nombre: 'Rústica', themeClass: 'theme-rustico' },
  costero: { id: 'costero', nombre: 'Costera', themeClass: 'theme-costero' },
  urbano: { id: 'urbano', nombre: 'Urbana', themeClass: 'theme-urbano' },
  romantico: { id: 'romantico', nombre: 'Romántica', themeClass: 'theme-romantico' },
  epoca: { id: 'epoca', nombre: 'Época', themeClass: 'theme-epoca' },
  moderno: { id: 'moderno', nombre: 'Moderna', themeClass: 'theme-moderno' },
};

export const ESTILO_DEFAULT: EstiloId = 'elegante';

/** Lista ordenada para el selector (usa el orden de ESTILOS). */
export const ESTILO_OPCIONES: EstiloMeta[] = ESTILOS.map((id) => ESTILO_META[id]);

/** Normaliza cualquier `template_id` de la invitación a un EstiloId válido. */
export function toEstilo(templateId: string | null | undefined): EstiloId {
  if (!templateId) return ESTILO_DEFAULT;
  const raw = templateId.toLowerCase();
  if ((ESTILOS as string[]).includes(raw)) return raw as EstiloId;
  // Compat con los template_id históricos (romantic/minimalist/rustic).
  const legacy: Record<string, EstiloId> = {
    romantic: 'romantico',
    romantica: 'romantico',
    minimalist: 'moderno',
    minimalista: 'moderno',
    modern: 'moderno',
    rustic: 'rustico',
    rustica: 'rustico',
    elegant: 'elegante',
    coastal: 'costero',
    costera: 'costero',
    urban: 'urbano',
    urbana: 'urbano',
    vintage: 'epoca',
    epoca: 'epoca',
  };
  return legacy[raw] ?? ESTILO_DEFAULT;
}

/** Clase de tema del DS para un estilo. */
export function themeClass(estilo: EstiloId): string {
  return ESTILO_META[estilo].themeClass;
}
